import * as Yup from "yup";
import { CreateSensorDto, SensorFormShape } from "@containers/Sensor/interfaces";
import { FORM_ERROR_MESSAGES } from "@shared/constants";
import { Hospital, SensorListItem, Station } from "@shared/models";
import { actions as stationActions, selectors as stationSelectors } from "@containers/Station/store/";
import { actions as hospitalActions, selectors as hospitalSelectors } from "@containers/Hospital/store/";
import { Option, USAGE_STATUS } from "@shared/interfaces";

const commonValidationSchema = {
  sensor_code: Yup.string().max(35, `Sensor ID ${FORM_ERROR_MESSAGES.LONG}`).required(FORM_ERROR_MESSAGES.REQUIRED),
  parameter: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED).nullable(),
  station: Yup.object()
    .shape({
      value: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
      label: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
    })
    .nullable(),
  type: Yup.string(),
  firmware_version: Yup.string(),
  hospital: Yup.object()
    .shape({
      value: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
      label: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
    })
    .nullable(),
};

export const createValidationSchemaSchema: Yup.SchemaOf<SensorFormShape> = Yup.object().shape({
  ...commonValidationSchema,
});

export const editValidationSchemaSchema: Yup.SchemaOf<Omit<SensorFormShape, "hospital_id">> = Yup.object().shape({
  ...commonValidationSchema,
});

export const getValidationSchema = (sensor?: SensorListItem | null) => {
  return sensor ? editValidationSchemaSchema : createValidationSchemaSchema;
};

export const getInitValues = (sensor: SensorListItem | null, defaultHospitalId: number | null): SensorFormShape => {
  const defaultHospital = defaultHospitalId
    ? {
        value: String(defaultHospitalId),
        label: "Default hospital",
      }
    : null;

  return {
    sensor_code: sensor?.sensor_code || "",
    parameter: sensor?.parameter || "",
    station: sensor?.station
      ? {
          label: String(sensor.station.station_code),
          value: String(sensor.station.id),
        }
      : null,
    type: sensor?.type || "",
    firmware_version: sensor?.firmware_version || "",
    hospital: sensor?.hospital
      ? {
          label: String(sensor.hospital.name),
          value: String(sensor.hospital.id),
        }
      : defaultHospital,
  };
};

export const getHandlers = (hospital: Option | null) => {
  const handlers = {
    station: {
      getData: (search: string) =>
        stationActions.getStations.request({
          search,
          page: 0,
          limit: 20,
          usage_status: USAGE_STATUS.AVAILABLE,
          hospital,
        }),
      selectData: stationSelectors.getStations,
      prepareOptionFunction: (item: Station): Option => {
        return { label: item.station_code, value: String(item.id) };
      },
      loadingAction: stationActions.getStations,
    },
    hospital: {
      getData: (search: string) =>
        hospitalActions.getHospitals.request({
          search,
          page: 0,
          limit: 20,
        }),
      selectData: hospitalSelectors.getHospitals,
      prepareOptionFunction: (item: Hospital): Option => {
        return { label: item.name, value: String(item.id) };
      },
      loadingAction: hospitalActions.getHospitals,
    },
  };

  return handlers;
};

export const prepareSubmitValue = (value: SensorFormShape): CreateSensorDto => ({
  parameter: value.parameter,
  sensor_code: value.sensor_code,
  station_id: value.station ? Number(value.station.value) : null,
  hospital_id: value.hospital ? Number(value.hospital.value) : undefined,
});
